import { DataPipelineCount, DatasetPipelineList } from '@api';
import { DATASET_TABLE_ID } from '@components/table/constants';
import { DataTablePaginationStore } from '@components/table/DataTablePagination/DataTablePaginationStore';
import { DataTableRenderer } from '@components/table/DataTableRenderer/DataTableRenderer';
import { useApi } from '@data-access';
import { MapperHelper, toLocalISOString, withQueryParams } from '@helpers';
import { FiltersHelper } from '@helpers/filters';
import { datasetInventoryCounterStore, viewSettingsStore } from '@stores';
import { RankingInfo } from '@tanstack/match-sorter-utils';
import {
  ColumnDef,
  ColumnFilter,
  ColumnFiltersState,
  ColumnSizingState,
  FilterFn,
  VisibilityState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { DataTableToolbar } from './_components/Toolbar/DatasetInventoryTableToolbar';
import styles from './DatasetInventoryTable.module.scss';

const DATASET_API_FILTER_IDS = new Set(['status', 'taskType', 'aiModelName']);

const DATASET_FILTER_BACKEND_MAP: Record<string, string> = {
  taskType: 'task_type',
  aiModelName: 'ai_model_name',
};

interface DataTableProps<TValue> {
  columns: ColumnDef<DatasetPipelineList, TValue>[];
  data: DatasetPipelineList[] | null;
  setData: React.Dispatch<React.SetStateAction<DatasetPipelineList[] | null>>;
  setCounts?: React.Dispatch<React.SetStateAction<DataPipelineCount | undefined>>;
  onRowSelect?: (row: DatasetPipelineList | null) => void;
  /** Extra API filters to be added to the API request */
  extraApiFilters?: Record<string, any>;
  /** Disable query sync (only in the inventory page) */
  disableQuerySync?: boolean;
  /** Pre-select a row by pipeline id on first data load (assessment step) */
  defaultSelectedId?: number | null;
  /** Custom content rendered in the empty-rows slot when there are no results and not loading */
  emptyContent?: React.ReactNode;
  /** Return a className to apply to a row (e.g. disabled styling in the selection step) */
  getRowClassName?: (row: DatasetPipelineList) => string | undefined;
  /** Hide "Back to default" and "Save filters" buttons (assessment selection step) */
  hideFilterPersistence?: boolean;
  /** Hide the "View settings" button */
  hideViewOptions?: boolean;
  viewOptionsContainer?: HTMLElement | null;
}

declare module '@tanstack/table-core' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

const DatasetInventoryTableInner = <TValue,>({
  columns,
  data,
  setData,
  setCounts,
  onRowSelect,
  extraApiFilters,
  disableQuerySync = false,
  defaultSelectedId,
  emptyContent,
  getRowClassName,
  hideFilterPersistence = false,
  hideViewOptions = false,
  viewOptionsContainer,
}: DataTableProps<TValue>) => {
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>(() => {
    if (defaultSelectedId && data?.length) {
      const row = data.find(r => r.id === defaultSelectedId);
      if (row) return { [String(row.id)]: true };
    }
    return {};
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [defaultFilters, setDefaultFilters] = useState<ColumnFilter[]>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  const { datasetInventoryId } = viewSettingsStore;

  const {
    counter,
    currentPage,
    loader,
    setCurrentPage,
    setPreviousPage,
    setNextPage,
    setCount,
    setLoader,
    resetStore,
  } = datasetInventoryCounterStore;

  const { query } = useRouter();
  const { api, datasetPiplineApi } = useApi();

  const showLoader = !columnVisibility || !data || loader || isLoading;

  const table = useReactTable({
    data: data ?? [],
    columns,
    filterFns: {
      fuzzy: MapperHelper.fuzzyFilter,
    },
    getRowId: row => String(row.id),
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility,
      rowSelection,
      columnFilters,
      globalFilter,
      columnSizing,
    },
    meta: {
      addRow: (newRow: DatasetPipelineList, index: number) => {
        setRowSelection({});
        setData(prevData => [...(prevData ?? []).slice(0, index), newRow, ...(prevData ?? []).slice(index)]);
      },
      removeRow: (id: number) => {
        setRowSelection({});
        setData(prevData => (prevData ?? []).filter(dataset => dataset.id !== id));
      },
      updateRow: (updatedRow: DatasetPipelineList) => {
        setData(prevData => (prevData ?? []).map(row => (row.id === updatedRow.id ? updatedRow : row)));
      },
    },
    defaultColumn: { minSize: 120 },
    enableRowSelection: true,
    enableSorting: false,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    autoResetPageIndex: false,
    enableMultiRowSelection: false,
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: MapperHelper.fuzzyFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    getFilteredRowModel: getFilteredRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  // Restore a previously saved selection on first data load (radio only)
  const defaultSelectionAppliedRef = useRef(
    // If rowSelection was pre-seeded by the lazy initializer, treat it as already applied
    defaultSelectedId != null && data?.some(r => r.id === defaultSelectedId),
  );

  useEffect(() => {
    if (!data || !data.length || !defaultSelectedId || defaultSelectionAppliedRef.current) return;
    const findRow = table.getExpandedRowModel().rows.find(r => r.original.id === defaultSelectedId);
    if (findRow) {
      defaultSelectionAppliedRef.current = true;
      setRowSelection({ [findRow.id]: true });
      onRowSelect?.(findRow.original);
    }
    // `table` intentionally omitted — mutable object, ref guard prevents re-execution
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, defaultSelectedId]);

  useEffect(() => {
    if (!onRowSelect) return;
    // Don't notify before data has loaded — avoids clearing parent's preserved selection on mount
    if (!data) return;
    const selectedKeys = Object.keys(rowSelection);
    if (selectedKeys.length === 0) {
      onRowSelect(null);
      return;
    }
    const selectedRow = table.getExpandedRowModel().rows.find(row => row.id === selectedKeys[0]);
    if (selectedRow) {
      onRowSelect(selectedRow.original);
    }
  }, [onRowSelect, rowSelection, table, data]);

  // Reset to page 1 when the user changes filters (skip the initial load where columnFilters goes null → value)
  const initialFiltersLoadedRef = useRef(false);
  useEffect(() => {
    if (columnFilters === null) return;
    if (!initialFiltersLoadedRef.current) {
      initialFiltersLoadedRef.current = true;
      return;
    }
    setCurrentPage(1);
  }, [columnFilters, setCurrentPage]);

  const fetchDatasetPipelines = useCallback(async () => {
    if (!datasetPiplineApi) return;
    if (columnFilters === null) return;

    const TEXT_FILTER_MAP: Record<string, string> = {
      taskName: 'task_name',
    };

    const DATE_RANGE_MAP: Record<string, [string, string]> = {
      startTime: ['start_time_after', 'start_time_before'],
      endTime: ['end_time_after', 'end_time_before'],
    };

    const textParams: Record<string, string> = {};

    const filters = columnFilters?.reduce((acc, filter) => {
      if (DATASET_API_FILTER_IDS.has(filter.id)) {
        acc[filter.id] = filter.value;
      } else if (TEXT_FILTER_MAP[filter.id]) {
        const val = (filter.value as string[])[0];
        if (val) textParams[TEXT_FILTER_MAP[filter.id]] = val;
      } else if (DATE_RANGE_MAP[filter.id]) {
        const { after, before } = (filter.value ?? {}) as { after?: Date; before?: Date };
        const [afterKey, beforeKey] = DATE_RANGE_MAP[filter.id];
        if (after) textParams[afterKey] = toLocalISOString(after);
        if (before) textParams[beforeKey] = toLocalISOString(before);
      }
      return acc;
    }, {} as Record<string, any>);

    const countsParams: Record<string, string | string[]> = { ...textParams };
    Object.entries(filters ?? {}).forEach(([key, value]) => {
      countsParams[DATASET_FILTER_BACKEND_MAP[key] ?? key] = value;
    });
    Object.entries(extraApiFilters ?? {}).forEach(([key, value]) => {
      countsParams[key] = value;
    });

    const apiCall =
      Object.keys(textParams).length > 0 ? withQueryParams(datasetPiplineApi, textParams) : datasetPiplineApi;
    const countsApiCall =
      setCounts && Object.keys(countsParams).length > 0
        ? withQueryParams(datasetPiplineApi, countsParams)
        : datasetPiplineApi;

    const [fetchedDatasets, newCounts] = await Promise.all([
      apiCall.apiDatasetPipelinesList({
        page: currentPage,
        pageSize: counter,
        ...extraApiFilters,
        ...filters,
      }),
      setCounts ? countsApiCall.apiDatasetPipelinesCountsRetrieve() : Promise.resolve(null),
    ]);
    if (setCounts && newCounts) setCounts(newCounts);
    if (!fetchedDatasets.previous) {
      setPreviousPage(null);
    } else {
      const urlObj = new URL(fetchedDatasets.previous);
      const page = urlObj.searchParams.get('page');
      setPreviousPage(Number(page));
    }
    if (!fetchedDatasets.next) {
      setNextPage(null);
    } else {
      const urlObj = new URL(fetchedDatasets.next);
      const page = urlObj.searchParams.get('page');
      setNextPage(Number(page));
    }
    setCount(fetchedDatasets.count);
    setData(fetchedDatasets.results as DatasetPipelineList[]);
    setLoader(false);
  }, [
    datasetPiplineApi,
    columnFilters,
    counter,
    currentPage,
    extraApiFilters,
    setCount,
    setCounts,
    setData,
    setLoader,
    setNextPage,
    setPreviousPage,
  ]);

  useEffect(() => {
    if (!datasetInventoryId) return;
    if (columnFilters !== null) return;
    if (disableQuerySync) {
      FiltersHelper.getDefaultFilters(
        viewSettingsStore.viewSettingsById[datasetInventoryId]?.urlParameters,
        setDefaultFilters,
        setIsLoading,
        setColumnFilters,
      );
    } else if (FiltersHelper.getFiltersFromUrl(query, [], setColumnFilters)) {
      setIsLoading(false);
    } else {
      FiltersHelper.getDefaultFilters(
        viewSettingsStore.viewSettingsById[datasetInventoryId]?.urlParameters,
        setDefaultFilters,
        setIsLoading,
        setColumnFilters,
      );
    }
  }, [api, columnFilters, datasetInventoryId, disableQuerySync, query, setColumnFilters, setIsLoading]);

  useEffect(() => {
    /** This is used to update the query params with the current column filters (only in the inventory page) */
    if (columnFilters === null) return;
    if (!disableQuerySync) {
      FiltersHelper.updateQuery(columnFilters);
    }
    if (defaultFilters?.length > 0 && columnFilters?.length > 0) {
      setIsLoading(false);
    }
  }, [columnFilters, defaultFilters, disableQuerySync, setIsLoading]);

  useEffect(() => {
    if (!datasetInventoryId) {
      setColumnVisibility({});
      return;
    }
    setColumnVisibility(viewSettingsStore.viewSettingsById[datasetInventoryId]?.columnSettings);
  }, [datasetInventoryId]);

  useEffect(
    () => () => {
      resetStore();
    },
    [resetStore],
  );

  useEffect(() => {
    fetchDatasetPipelines();
  }, [fetchDatasetPipelines]);

  // poll dataset pipelines to get the latest data
  useEffect(() => {
    if (!data || !api) return undefined;

    const hasRunningOrPendingDatasets = data.some(row => ['pending', 'running'].includes(row.status));

    if (!hasRunningOrPendingDatasets) {
      return undefined;
    }

    const interval = setInterval(async () => {
      try {
        const updatedDatasets = await datasetPiplineApi.apiDatasetPipelinesList({
          page: currentPage,
          pageSize: counter,
          ...extraApiFilters,
          ...columnFilters?.reduce((acc, filter) => {
            if (DATASET_API_FILTER_IDS.has(filter.id)) {
              acc[filter.id] = filter.value;
            }
            return acc;
          }, {} as Record<string, any>),
        });

        setData(updatedDatasets.results as any);
      } catch (err) {
        console.error('Error polling data generation rows', err);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [api, columnFilters, currentPage, counter, datasetPiplineApi, data, extraApiFilters, setData]);

  return (
    <div className={styles.page}>
      <div className={styles.wrapper}>
        <DataTableToolbar
          table={table}
          tableId={datasetInventoryId}
          tableName={DATASET_TABLE_ID}
          columnFilters={columnFilters}
          defaultFilters={defaultFilters}
          setColumnFilters={setColumnFilters}
          setDefaultFilters={setDefaultFilters}
          hideFilterPersistence={hideFilterPersistence}
          hideViewOptions={hideViewOptions}
          viewOptionsContainer={viewOptionsContainer}
        />
        <div className={styles.table}>
          <DataTableRenderer
            table={table}
            columns={columns}
            tableId={DATASET_TABLE_ID}
            bodyId={`${DATASET_TABLE_ID}Body`}
            rowId="datasetRow"
            loader={showLoader}
            emptyMessage={emptyContent ?? 'No results.'}
            rowClassName={getRowClassName}
            onRowClick={
              onRowSelect
                ? row => {
                    row.toggleSelected(true);
                    onRowSelect(row.original);
                  }
                : undefined
            }
            footer={
              <DataTablePaginationStore
                table={table}
                store={datasetInventoryCounterStore}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

export const DatasetInventoryTable = observer(DatasetInventoryTableInner);
